import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Gift, Package, Loader2, ChevronRight } from 'lucide-react';
import { HamperOptionsDialog } from './HamperOptionsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HAMPER_IMAGES = {
//  'smallPVC-hamper':       'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'smallPVC-hamper':       '/images/products/smallPVC-hamper.jpg',
  'kraftbox-hamper':       '/images/products/kraftbox-hamper.jpg',
  'base8-hamper':          '/images/products/base8-hamper.jpg',
  'base10-hamper':         '/images/products/base10-hamper.jpg',
  'pvc-medium-hamper':     '/images/products/pvc-medium-hamper.jpg',
  'corrugated-box-hamper': '/images/products/corrugated-box-hamper.jpg',
  //'kraftbox-hamper':       'https://images.unsplash.com/photo-1766375885762-41cfbacde261?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  //'base8-hamper':          'https://images.unsplash.com/photo-1720788810305-85fe22e6c3a2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  //'base10-hamper':         'https://images.pexels.com/photos/17878238/pexels-photo-17878238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  //'pvc-medium-hamper':     'https://images.unsplash.com/photo-1711645973621-fc4929ed9d5a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  //'corrugated-box-hamper': 'https://images.unsplash.com/photo-1711645973555-d03091cc4d0a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
};
const DEFAULT_IMG = 'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export const HamperOptionsSection = () => {
  const [hampers, setHampers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hamper-options`)
      .then(r => setHampers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center items-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div data-testid="hamper-options-section">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-sm font-medium mb-3">
          <Gift className="h-4 w-4" />
          Build Your Perfect Gift
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-accent mb-2">Curated Gift Hampers</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          Pick a hamper, choose your variant, then personalise every item inside.
        </p>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hamper-options-grid">
        {hampers.map((hamper) => {
          const variantCount = hamper.selection_groups?.[0]?.options?.length ?? 0;
          return (
            <Card
              key={hamper.id}
              className="group overflow-hidden border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
              onClick={() => setSelected(hamper)}
              data-testid={`hamper-option-card-${hamper.id}`}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={HAMPER_IMAGES[hamper.id] || DEFAULT_IMG}
                  alt={hamper.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded-full">
                    Starts at ₹{hamper.base_price}
                  </span>
                </div>
                {variantCount > 0 && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-white/90 text-amber-800 text-xs font-medium border-0">
                      {variantCount} variant{variantCount !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="p-4">
                <h3 className="font-bold text-accent text-lg mb-1">{hamper.name}</h3>
                {hamper.tagline && (
                  <p className="text-sm text-muted-foreground mb-3">{hamper.tagline}</p>
                )}

                {/* Variant preview chips */}
                {variantCount > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {hamper.selection_groups[0].options.slice(0, 2).map(v => (
                      <span key={v.id} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full truncate max-w-[160px]">
                        {v.name}
                      </span>
                    ))}
                    {variantCount > 2 && (
                      <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                        +{variantCount - 2} more
                      </span>
                    )}
                  </div>
                )}

                <Button
                  className="w-full bg-primary hover:bg-primary/90 rounded-full text-sm font-medium"
                  onClick={(e) => { e.stopPropagation(); setSelected(hamper); }}
                  data-testid={`customize-hamper-option-${hamper.id}`}
                >
                  <Package className="h-4 w-4 mr-2" />
                  Customise &amp; Add to Cart
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <HamperOptionsDialog
        hamper={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};
